import {
	Cartesian2,
	Cartesian3,
	CallbackProperty,
	Color,
	ClassificationType,
	GridMaterialProperty,
	PolylineArrowMaterialProperty,
	PolygonHierarchy,
	Rectangle,
	SceneTransforms,
} from 'cesium';
import { HistoryItem } from '@/classes/History';
import { Style } from '@/classes/Entity';

const VIEWER_STATE = Object.freeze({
	/** The user can freely navigate the globe and select objects by clicking on them */
	DEFAULT: 'DEFAULT',
	/** Shape authoring workflow allowing the user to author a shape */
	SAW: 'SAW',
	/** Whenever a user clicks a "place" event is fired */
	PLACEMENT: 'PLACEMENT',
	/** OSM selection workflow for saving locations/camera views */
	OSM_SELECT: 'OSM_SELECT',
	ZOOM: 'ZOOM',
});
const AUTHORING_SHAPE = Object.freeze({
	PATH: 'path',
	POLYGON: 'polygon',
});
const DEFAULT_STYLE = new Style();

export function createShapeAuthoringEntities() {
	const cesiumViewer = this.getCesiumData().viewer;
	// create non-reactive shape authoring workflow data structures
	this.shapeAuthoringWorkflow.polygonEntity = cesiumViewer.entities.add({
		polygon: {
			hierarchy: new CallbackProperty(
				() =>
					new PolygonHierarchy(
						this.shapeAuthoringWorkflow.vertices.map((v) =>
							v.clone()
						)
					),
				false
			),
			material: new Color(1, 0.5, 1, 0.5),
		},
		show: false,
	});

	this.shapeAuthoringWorkflow.pathEntity = cesiumViewer.entities.add({
		corridor: {
			positions: new CallbackProperty(
				() =>
					this.shapeAuthoringWorkflow.vertices.map((v) => v.clone()),
				false
			),
			width: 1,
			material: new Color(1, 0.5, 1, 0.5),
			classificationType: ClassificationType.TERRAIN,
		},
		show: false,
	});

	this.shapeAuthoringWorkflow.selector = cesiumViewer.entities.add({
		name: 'Rectangle selector',
		show: false,
		rectangle: {
			coordinates: new CallbackProperty((time, result) => {
				return Rectangle.clone(this.rectangleSelector);
			}, false),
			material: Color.BLACK.withAlpha(0.5),
		},
	});

	this.shapeAuthoringWorkflow.lineEntity = cesiumViewer.entities.add({
		id: 'lineEntity',
		show: false,
		polyline: {
			positions: new CallbackProperty(
				() => Cartesian3.fromDegreesArray([
					this.modelWorkflow.position.longitude, this.modelWorkflow.position.latitude,
					this.mouseEndPosition.longitude, this.mouseEndPosition.latitude
				]).map(v => v.clone()),
				false
			),
			width: 9.0,
			material: new PolylineArrowMaterialProperty(Color.ORANGE),
			clampToGround: true
		}
	});

	this.shapeAuthoringWorkflow.placementMarker = cesiumViewer.entities.add({
		id: 'placementMarker',
		position: new CallbackProperty(
			() => {return Cartesian3.fromDegrees(this.mouseEndPosition.longitude, this.mouseEndPosition.latitude).clone()},
			false
		),
		show: false,
		ellipse: {
			semiMinorAxis: 3,
			semiMajorAxis: 3,
			material: new GridMaterialProperty({
				color: Color.YELLOW,
				cellAlpha: 0.2,
				lineCount: new Cartesian2(8, 8),
				lineThickness: new Cartesian2(2.0, 2.0),
			}),
			clampToGround: true,
		},
	});
	
	this.shapeAuthoringWorkflow.modelBoundingBox = cesiumViewer.entities.add({
		id: 'modelBoundingBox',
		name: 'Yellow box outline',
		// eslint-disable-next-line new-cap
		position: new CallbackProperty(
			() => Cartesian3.fromDegrees(this.mouseEndPosition.longitude, this.mouseEndPosition.latitude).clone(),
			false
		),
		orientation: new CallbackProperty(() => {
			return this.createOrientation(this.modelWorkflow.rotation).clone();
		}, false),
		box: {
			height: this.modelWorkflow.height,
			heightReference: 1,
			dimensions: new CallbackProperty(() =>
				new Cartesian3(
					this.modelWorkflow.dimensions.depth,
					this.modelWorkflow.dimensions.width,
					this.modelWorkflow.dimensions.height
				).clone(), false),
			fill: false,
			outline: true,
			outlineColor: Color.YELLOW,
			classificationType: ClassificationType.TERRAIN,
		},
		show: false,
	});
}

/**
 * Enter shape authoring workflow
 * @param {AUTHORING_SHAPE} shapeType the type of shape to author
 * @param {Style} [shapeStyle] the style of the shape to be created
 * @return {Promise<?Cartesian3[]>} the created shape, null when the workflow was aborted
 */
export async function enterShapeAuthoringWorkflow(
	shapeType,
	shapeStyle = DEFAULT_STYLE
) {
	const cesiumViewer = this.getCesiumData().viewer;
	if (this.state !== VIEWER_STATE.DEFAULT)
		throw new Error(
			'Cannot enter shape authoring workflow from current state'
		);

	// apply shape style
	cesiumViewer.scene.requestRenderMode = false;

	this.shapeAuthoringWorkflow.polygonEntity.polygon.material.color.setValue(
		shapeStyle.fillColor
	);
	this.shapeAuthoringWorkflow.pathEntity.corridor.material.color.setValue(
		shapeStyle.lineColor
	);
	this.shapeAuthoringWorkflow.pathEntity.corridor.width.setValue(
		shapeStyle.lineWidth
	);

	this.state = VIEWER_STATE.SAW;
	this.shapeAuthoringWorkflow.shape = shapeType;
	this.$nextTick().then(() =>
		this.$refs.shapeAuthoringToolbar.positionAtOrigin()
	);

	if (shapeType === AUTHORING_SHAPE.PATH)
		this.shapeAuthoringWorkflow.pathEntity.show = true;
	else if (shapeType === AUTHORING_SHAPE.POLYGON)
		this.shapeAuthoringWorkflow.polygonEntity.show = true;
	else throw new TypeError('Invalid shape type provided');

	return new Promise((resolve) => {
		this.shapeAuthoringWorkflow.resolve = resolve;
	});
}
/**
 * Enter placement workflow, with every click a 'place' event is fired
 * @return {Promise<Cartesian3[]>} the placed positions
 */
export async function enterPlacementWorkflow() {
	if (this.state !== VIEWER_STATE.DEFAULT)
		throw new Error('Cannot enter placement workflow from current state');
	this.state = VIEWER_STATE.PLACEMENT;
	this.placementWorkflow.positions.length = 0;
	this.placementWorkflow.history.clear();
	return new Promise((resolve) => {
		this.placementWorkflow.resolve = resolve;
	});
}
// ////////////////////////////////////////////////////
// private functions and event handlers from here on //
// ////////////////////////////////////////////////////

/**
 * Clear vertex drag state for shape authoring workflow
 * @private
 */
export function onMouseUp() {
	if (this.shapeAuthoringWorkflow.draggedVertexId !== -1)
		this.onShapeAuthoringVertexDragEnd();
}
/**
 * Push reference of the first vertex into the vertex array to close the loop, create history item
 * @private
 */
export function onShapeAuthoringVertexCloseLoop() {
	const historyItem = new HistoryItem(
		this.$t('shape-authoring-workflow.history.remove-vertex'),
		() => {
			this.shapeAuthoringWorkflow.vertices.push(
				this.shapeAuthoringWorkflow.vertices[0]
			);
			this.shapeAuthoringWorkflow.screenVertices.push(new Cartesian2());
			this.updateShapeAuthoringVertices();
		},
		() => {
			this.shapeAuthoringWorkflow.vertices.pop();
			this.shapeAuthoringWorkflow.screenVertices.pop();
			this.updateShapeAuthoringVertices();
		}
	);
	historyItem.redo();
	this.shapeAuthoringWorkflow.history.push(historyItem);
	this.shapeAuthoringWorkflow.selectedVertexId = -1;
}
/**
 * Write updated position to vertex and update screen vertices
 * @private
 */
export function onShapeAuthoringVertexUpdate(newPos, index) {
	Cartesian3.clone(newPos, this.shapeAuthoringWorkflow.vertices[index]);
	this.updateShapeAuthoringVertices();
}
/**
 * Delete vertex at given index, create history item
 * @param {Number} index
 * @private
 */
export function onShapeAuthoringVertexDelete(index) {
	const vertex = this.shapeAuthoringWorkflow.vertices[index];
	const historyItem = new HistoryItem(
		this.$t('shape-authoring-workflow.history.remove-vertex'),
		() => {
			this.shapeAuthoringWorkflow.vertices.splice(index, 1);
			this.shapeAuthoringWorkflow.screenVertices.pop();
			this.updateShapeAuthoringVertices();
		},
		() => {
			this.shapeAuthoringWorkflow.vertices = [
				...this.shapeAuthoringWorkflow.vertices.slice(0, index),
				vertex,
				...this.shapeAuthoringWorkflow.vertices.slice(index),
			];
			this.shapeAuthoringWorkflow.screenVertices.push(new Cartesian2());
			this.updateShapeAuthoringVertices();
		}
	);
	historyItem.redo();
	this.shapeAuthoringWorkflow.history.push(historyItem);
}
/**
 * Splits given line segment into two, creates history item and enters vertex drag mode
 * @param {Number} index The segment index
 * @private
 */
export function splitSegment(index) {
	const v0 = this.shapeAuthoringWorkflow.vertices[index];
	const v1 = this.shapeAuthoringWorkflow.vertices[index + 1];
	const historyItem = new HistoryItem(
		this.$t('shape-authoring-workflow.history.insert-vertex'),
		() => {
			this.shapeAuthoringWorkflow.vertices = [
				...this.shapeAuthoringWorkflow.vertices.slice(0, index + 1),
				new Cartesian3(
					v0.x + (v1.x - v0.x) / 2,
					v0.y + (v1.y - v0.y) / 2,
					v0.z + (v1.z - v0.z) / 2
				),
				...this.shapeAuthoringWorkflow.vertices.slice(index + 1),
			];
			this.shapeAuthoringWorkflow.screenVertices.push(new Cartesian2());
			this.updateShapeAuthoringVertices();
		},
		() => {
			this.shapeAuthoringWorkflow.vertices.splice(index + 1, 1);
			this.shapeAuthoringWorkflow.screenVertices.pop();
			this.updateShapeAuthoringVertices();
		}
	);
	historyItem.redo();
	this.shapeAuthoringWorkflow.history.push(historyItem);
	this.onShapeAuthoringVertexDragBegin(index + 1);
}
/**
 * Set vertex dragging state
 * @private
 */
export function onShapeAuthoringVertexDragBegin(vertexIndex) {
	const vertex = this.shapeAuthoringWorkflow.vertices[vertexIndex];
	if (!vertex) throw new RangeError('Invalid vertex id');
	this.shapeAuthoringWorkflow.draggedVertexOrigin = vertex.clone();
	this.shapeAuthoringWorkflow.selectedVertexId = this.shapeAuthoringWorkflow.draggedVertexId = vertexIndex;
}
/**
 * Resolve dragged vertex state and create history item if applicable
 * @private
 */
export function onShapeAuthoringVertexDragEnd() {
	const vertex = this.shapeAuthoringWorkflow.vertices[
		this.shapeAuthoringWorkflow.draggedVertexId
	];
	// create history item for vertex move if the vertex actually moved
	if (
		!Cartesian3.equals(
			this.shapeAuthoringWorkflow.draggedVertexOrigin,
			vertex
		)
	) {
		const vid = this.shapeAuthoringWorkflow.draggedVertexId;
		const startPosition = this.shapeAuthoringWorkflow.draggedVertexOrigin;
		const endPosition = vertex.clone();
		this.shapeAuthoringWorkflow.history.push(
			new HistoryItem(
				this.$t('shape-authoring-workflow.history.move-vertex'),
				() => {
					endPosition.clone(
						this.shapeAuthoringWorkflow.vertices[vid]
					);
					this.updateShapeAuthoringVertices();
				},
				() => {
					startPosition.clone(
						this.shapeAuthoringWorkflow.vertices[vid]
					);
					this.updateShapeAuthoringVertices();
				}
			)
		);
	}
	this.shapeAuthoringWorkflow.draggedVertexId = -1;
}
/**
 * Update vertices
 * @private
 */
export function updateShapeAuthoringVertices() {
	const cesiumViewer = this.getCesiumData().viewer;
	for (let i = 0; i < this.shapeAuthoringWorkflow.vertices.length; i++) {
		SceneTransforms.wgs84ToWindowCoordinates(
			cesiumViewer.scene,
			this.shapeAuthoringWorkflow.vertices[i],
			this.shapeAuthoringWorkflow.screenVertices[i]
		);
	}
}
/**
 * Called when the user exits the shape authoring workflow
 * @param {Boolean} confirmed whether the user confirmed the shape or cancelled the workflow
 * @private
 */
export function onExitShapeAuthoringWorkflow(confirmed) {
	const cesiumViewer = this.getCesiumData().viewer;
	this.shapeAuthoringWorkflow.history.clear();
	this.shapeAuthoringWorkflow.pathEntity.show = false;
	this.shapeAuthoringWorkflow.polygonEntity.show = false;
	let shapeVertices = null;
	if (confirmed) shapeVertices = this.shapeAuthoringWorkflow.vertices.slice();
	this.shapeAuthoringWorkflow.vertices.length = 0;
	this.shapeAuthoringWorkflow.screenVertices.length = 0;
	this.state = VIEWER_STATE.DEFAULT;
	this.shapeAuthoringWorkflow.resolve(shapeVertices);
	cesiumViewer.scene.requestRenderMode = true;
}
/**
 * Resolve placement workflow and go back to default state
 * @private
 */
export function onExitPlacementWorkflow() {
	this.state = VIEWER_STATE.DEFAULT;
	this.placementWorkflow.resolve(this.placementWorkflow.positions);
}
